import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, InputAdornment,
  Alert, CircularProgress, Stack, Chip,
} from '@mui/material';
import VisibilityIcon         from '@mui/icons-material/Visibility';
import VisibilityOffIcon      from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon      from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon       from '@mui/icons-material/LockOutlined';
import LoginIcon              from '@mui/icons-material/Login';
import VerifiedUserIcon       from '@mui/icons-material/VerifiedUser';
import AccountBalanceIcon     from '@mui/icons-material/AccountBalance';
import GroupsIcon             from '@mui/icons-material/Groups';
import DescriptionIcon        from '@mui/icons-material/Description';
import ShieldIcon             from '@mui/icons-material/Shield';
import AutoAwesomeIcon        from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon           from '@mui/icons-material/ArrowBack';
import { login as apiLogin, setAuth } from '../services/api';

export default function Login({ onLogin, onBackToLanding }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [busy, setBusy]         = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!username.trim()) { setError('Username is required'); return; }
    if (!password)        { setError('Password is required'); return; }

    setBusy(true);
    try {
      const res = await apiLogin(username.trim().toLowerCase(), password);
      const { token, user, expires_in } = res.data;
      if (!token) {
        setError('Login failed — server did not return a token.');
        setBusy(false);
        return;
      }
      const session = {
        token,
        expires_in,
        username:  user?.username || username.trim(),
        full_name: user?.full_name,
        role:      user?.role,
        loggedInAt: new Date().toISOString(),
      };
      setAuth(session);
      onLogin(session);
    } catch (err) {
      setError(err.userMessage || err.response?.data?.message || 'Login failed. Please try again.');
      setBusy(false);
    }
  };

  const features = [
    { icon: <AccountBalanceIcon />, label: 'Statutory compliance — PF, ESI, PT, TDS' },
    { icon: <GroupsIcon />,         label: 'Multi-company, multi-LE workforce' },
    { icon: <DescriptionIcon />,    label: 'Form 16, payslips, bank file generation' },
    { icon: <ShieldIcon />,         label: 'Audit-ready records with effective dating' },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      bgcolor: '#F3F4F6',
    }}>
      {/* Left panel — branding (hidden on small screens) */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' },
        flex: 1.2,
        background: `
          linear-gradient(135deg, rgba(15, 23, 42, 0.55) 0%, rgba(15, 23, 42, 0.40) 50%, rgba(15, 23, 42, 0.65) 100%),
          url('https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=1600&q=80&auto=format&fit=crop') center/cover no-repeat
        `,
        backgroundColor: '#0F172A', // dark slate fallback
        color: 'white',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: 6,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative shapes */}
        <Box sx={{
          position: 'absolute', top: -80, right: -80, width: 280, height: 280,
          borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -100, left: -100, width: 360, height: 360,
          borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)',
        }} />

        {/* Top — logo */}
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '12px',
            bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1.5px solid rgba(255,255,255,0.3)',
          }}>
            <Typography fontSize={22} fontWeight={900}>V</Typography>
          </Box>
          <Box>
            <Typography fontSize={20} fontWeight={900} sx={{ letterSpacing: -0.3 }}>
              VVSPL HRMS
            </Typography>
            <Typography fontSize={11} sx={{ opacity: 0.85, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Payroll Management
            </Typography>
          </Box>
        </Stack>

        {/* Middle — pitch */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important', color: 'white !important' }} />}
            label="ENTERPRISE PAYROLL FOR INDIA"
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.18)', color: 'white',
              fontWeight: 700, fontSize: 10, letterSpacing: 0.8,
              mb: 2.5,
            }}
          />
          <Typography fontSize={36} fontWeight={800} lineHeight={1.15} sx={{ letterSpacing: -0.8, mb: 2 }}>
            Payroll done right.<br />
            Every cycle.
          </Typography>
          <Typography fontSize={14} sx={{ opacity: 0.9, mb: 4, maxWidth: 420, lineHeight: 1.6 }}>
            A full HRMS + payroll engine purpose-built for Indian compliance —
            from employee onboarding to Form 16 generation, with statutory
            calculations baked in.
          </Typography>

          <Stack spacing={1.5} sx={{ maxWidth: 440 }}>
            {features.map((f, i) => (
              <Stack key={i} direction="row" alignItems="center" gap={1.5}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '8px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {React.cloneElement(f.icon, { sx: { fontSize: 17, color: 'white' } })}
                </Box>
                <Typography fontSize={13.5} sx={{ opacity: 0.95 }}>{f.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* Bottom — footer */}
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          sx={{ position: 'relative', zIndex: 1, opacity: 0.85 }}>
          <Typography fontSize={11.5}>© 2026 VVSPL HRMS</Typography>
          <Stack direction="row" gap={2}>
            <Typography fontSize={11.5}>Privacy</Typography>
            <Typography fontSize={11.5}>Terms</Typography>
            <Typography fontSize={11.5}>Support</Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Right panel — login form */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 3, sm: 6 },
      }}>
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Mobile-only logo */}
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 4, display: { xs: 'flex', md: 'none' } }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: '10px',
              background: 'linear-gradient(135deg,#16a34a,#22c55e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography fontSize={18} fontWeight={900} color="white">V</Typography>
            </Box>
            <Typography fontSize={18} fontWeight={900} color="#111827">VVSPL HRMS</Typography>
          </Stack>

          {onBackToLanding && (
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBackToLanding}
              sx={{
                mb: 3, textTransform: 'none', color: '#64748B', fontWeight: 600,
                '&:hover': { color: '#16a34a', bgcolor: '#F0FDF4' }
              }}
            >
              Back to Presentation Overview
            </Button>
          )}

          <Box sx={{ mb: 4 }}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <VerifiedUserIcon sx={{ fontSize: 22, color: '#16a34a' }} />
              <Chip label="SECURE LOGIN" size="small" sx={{
                height: 20, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.5,
                bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
              }} />
            </Stack>
            <Typography fontSize={28} fontWeight={800} color="#111827" sx={{ letterSpacing: -0.5, mb: 0.8 }}>
              Welcome back
            </Typography>
            <Typography fontSize={13.5} color="#6B7280">
              Sign in to access your payroll dashboard
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {error && (
              <Alert severity="error" sx={{
                mb: 2.5, borderRadius: '10px', fontSize: 13,
                '& .MuiAlert-icon': { fontSize: 18 },
              }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={busy}
              autoComplete="username"
              autoFocus
              sx={{ mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#FAFBFC', borderRadius: '10px',
                  '& fieldset': { borderColor: '#E5E7EB', borderWidth: 1.5 },
                  '&:hover fieldset': { borderColor: '#22c55e' },
                  '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: 2 },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#16a34a' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoComplete="current-password"
              sx={{ mb: 3,
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#FAFBFC', borderRadius: '10px',
                  '& fieldset': { borderColor: '#E5E7EB', borderWidth: 1.5 },
                  '&:hover fieldset': { borderColor: '#22c55e' },
                  '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: 2 },
                },
                '& .MuiInputLabel-root.Mui-focused': { color: '#16a34a' },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPass(s => !s)}
                      edge="end" size="small" disabled={busy}
                      sx={{ color: '#94A3B8', '&:hover': { color: '#22c55e' } }}
                    >
                      {showPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={busy}
              startIcon={busy
                ? <CircularProgress size={16} sx={{ color: 'white' }} />
                : <LoginIcon sx={{ fontSize: 18 }} />}
              sx={{
                py: 1.5, borderRadius: '10px', fontWeight: 700, fontSize: 14,
                letterSpacing: 0.3, textTransform: 'none',
                background: 'linear-gradient(135deg,#15803d,#22c55e)',
                boxShadow: '0 4px 20px rgba(34,197,94,0.35)',
                '&:hover': {
                  background: 'linear-gradient(135deg,#166534,#16a34a)',
                  boxShadow: '0 6px 24px rgba(34,197,94,0.45)',
                },
                '&.Mui-disabled': {
                  background: 'linear-gradient(135deg,#15803d,#22c55e)',
                  color: 'rgba(255,255,255,0.8)',
                },
              }}
            >
              {busy ? 'Signing in…' : 'Sign In'}
            </Button>

          </Box>

          <Typography fontSize={11} color="#9CA3AF" align="center" sx={{ mt: 4 }}>
            By signing in, you agree to our Terms of Service and Privacy Policy.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
